import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdTrendingUp, MdTrendingDown } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import { fetchManufacturerVenue } from '../../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ManufacturerVenueDetail = () => {
    const { venueId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchManufacturerVenue(venueId);
                setData(response);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [venueId]);

    if (loading) {
        return <LoadingMessage>{t('ManufacturerVenueDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('ManufacturerVenueDetail.error')}</ErrorMessage>;
    }

    const { venue, stats, top_items: topItems } = data;

    const statsCards = [
        {
            key: 'sell_in',
            label: t('ManufacturerVenueDetail.stats.sellIn'),
            value: `${formatResolvedAmount({
                eurAmount: stats.sell_in_value,
                localAmount: stats.sell_in_value_local ?? stats.sell_in_local,
                localCurrencyId: stats.sell_in_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.sell_in_currency_code ?? stats.currency_code,
            })} / ${stats.sell_in_quantity} ${t('ManufacturerVenueDetail.stats.units')}`,
            icon: <MdTrendingDown className="icon" />,
            color: 'venues',
        },
        {
            key: 'sell_through',
            label: t('ManufacturerVenueDetail.stats.sellThrough'),
            value: `${formatResolvedAmount({
                eurAmount: stats.sell_through_value,
                localAmount: stats.sell_through_value_local ?? stats.sell_through_local,
                localCurrencyId: stats.sell_through_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.sell_through_currency_code ?? stats.currency_code,
            })} / ${stats.sell_through_quantity} ${t('ManufacturerVenueDetail.stats.units')}`,
            icon: <MdTrendingUp className="icon" />,
            color: 'transactions',
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <BackButton onClick={() => navigate('/venues')}>
                    <MdArrowBack /> {t('ManufacturerVenueDetail.back')}
                </BackButton>
                <TitleBlock>
                    <PageTitle>{venue.name}</PageTitle>
                    <Subtitle>{t('ManufacturerVenueDetail.subtitle', { city: venue.city || '' })}</Subtitle>
                </TitleBlock>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    {statsCards.map((card) => (
                        <StatCard
                            key={card.key}
                            icon={card.icon}
                            title={card.label}
                            value={card.value}
                            color={card.color}
                        />
                    ))}
                </StatsGrid>

                <InfoGrid>
                    <InfoCard>
                        <SectionTitle>{t('ManufacturerVenueDetail.contact.title')}</SectionTitle>
                        <InfoLine>{venue.address}</InfoLine>
                        <InfoLine>{[venue.city, venue.country].filter(Boolean).join(', ')}</InfoLine>
                        {venue.email && <InfoLine>{venue.email}</InfoLine>}
                        {venue.phone && <InfoLine>{venue.phone}</InfoLine>}
                        {venue.website && (
                            <InfoLine>
                                <a href={venue.website} target="_blank" rel="noreferrer">{venue.website}</a>
                            </InfoLine>
                        )}
                        {stats.last_transaction_at && (
                            <InfoLine>
                                {t('ManufacturerVenueDetail.lastActivity', { date: new Date(stats.last_transaction_at).toLocaleString() })}
                            </InfoLine>
                        )}
                    </InfoCard>
                    <InfoCard>
                        <SectionTitle>{t('ManufacturerVenueDetail.topItems.title')}</SectionTitle>
                        {topItems.length === 0 && <MutedText>{t('ManufacturerVenueDetail.topItems.empty')}</MutedText>}
                        {topItems.length > 0 && (
                            <TopItems>
                                {topItems.map((item) => (
                                    <li key={item.id}>
                                        <strong>{item.name}</strong>
                                        <span>{t('ManufacturerVenueDetail.topItems.metrics', {
                                            sell: item.sell_through_quantity,
                                            value: formatResolvedAmount({
                                                eurAmount: item.sell_through_value,
                                                localAmount: item.sell_through_value_local,
                                                localCurrencyId: item.sell_through_currency_id ?? stats.sell_through_currency_id ?? stats.currency_id,
                                                localCurrencyCode: item.sell_through_currency_code ?? stats.sell_through_currency_code ?? stats.currency_code,
                                            }),
                                        })}</span>
                                    </li>
                                ))}
                            </TopItems>
                        )}
                    </InfoCard>
                </InfoGrid>
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerVenueDetail;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
    margin-top: 1.875rem;
`;

const InfoCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 12px;
    padding: 1.25rem;
    background: #fff;
`;

const SectionTitle = styled.h3`
    margin-top: 0;
    margin-bottom: 0.75rem;
    color: rgba(130, 22, 146, 1);
`;

const InfoLine = styled.p`
    margin: 0 0 0.375rem;
    color: ${({ theme }) => theme.colors.textPrimary || '#222'};
`;

const MutedText = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#777'};
`;

const TopItems = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    li {
        border: 1px solid rgba(130, 22, 146, 0.15);
        border-radius: 8px;
        padding: 0.625rem;
    }

    strong {
        display: block;
        margin-bottom: 0.25rem;
    }

    span {
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#777'};
    }
`;
